import React, { useEffect, useMemo, useState } from 'react';

import { useSpring, animated } from '@react-spring/web';
import * as Sentry from '@sentry/nextjs';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { useAccountActions, useCustomer } from '@techstyle/react-accounts';
import { useAsset } from '@techstyle/react-assets';
import { useProductBrowser } from '@techstyle/react-components';
import { FormattedMessage, useIntl } from '@techstyle/react-intl';
import { useDomain } from '@techstyle/redux-core';

import { BasicButton } from '../../styles/button';
import {
  getAggregationValues,
  applyProfileSizeFilters,
} from '../../utils/productSizes';
import {
  getInfoSizeList,
  getBandSizeList,
  getCupSizeList,
  getNonBraSizes,
} from '../../utils/sizes';
import { useStepper } from '../../utils/stepper';
import { Component as SizeGuideModal } from '../SizeGuideModal';
import { Component as SizeInput } from '../SizeInput';
import Stepper from '../Stepper/Stepper';

const StyledSizeInput = styled(SizeInput)`
  background: ${({ theme }) => theme.colors.gray200};
  border-radius: 4px;
  padding: 12px;
`;

const StyledTextWrapper = styled(SizeInput.TextWrapper)`
  margin: 0 0 ${({ theme }) => theme.spacing.small}px;
  width: 100%;
`;

const StyledSmallText = styled(SizeInput.SmallText)`
  padding: 0 ${({ theme }) => theme.spacing.tiny}px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.gray150};
  letter-spacing: 0.3px;
  text-transform: none;
  margin-bottom: 18px;
  width: 100%;
  height: 50px;
`;

const StyledFinalStepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.gray200};
  border-radius: 4px;
  height: 64px;
`;

const StyledFinalStepText = styled.span`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.tiny}px
    ${({ theme }) => theme.spacing.large}px;
  background: ${({ theme }) => theme.colors.gray150};
  border-radius: 2px;
  font-size: 14px;
`;

const StyledLargeText = styled(SizeInput.LargeText)`
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 0.75px;
  line-height: 14px;
  text-transform: uppercase;
`;

const StyledIconWrapper = styled(SizeInput.IconWrapper)`
  margin-bottom: 13px;
`;

const SavedButton = styled(BasicButton)`
  padding: ${({ theme }) => theme.spacing.tiny}px 12px;
  background: ${({ theme }) => theme.colors.default};
  border-radius: 38px;
  width: 95px;
  height: 28px;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: 1px;
  border-color: transparent;
  margin-bottom: 28px;
  margin-top: ${({ theme }) => theme.spacing.small}px;
`;

const BoldText = styled.b``;

const StyledSelectWrapper = styled(SizeInput.SelectWrapper)`
  margin-bottom: 20px;
  gap: 12px;
`;

const BraSelectWrapper = styled(StyledSelectWrapper)`
  margin-bottom: 0px;

  @media (min-width: ${({ theme }) => theme.breakpoints.large}px) {
    width: 100%;
  }
`;

const BraSizeSelection = styled(SizeInput.SizeSelection)`
  ${SizeInput.SizeSelection.StyledSelect} {
    width: 150px;
  }
`;

const inputStyle = css`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.lavender250};
  background-color: ${({ theme }) => theme.colors.white};
  font-size: 16px;
  line-height: 19px;
  margin: 0;
`;

const StyledSizeGuideBtn = styled(BasicButton)`
  color: ${({ theme }) => theme.colors.active};
  font-size: 12px;
  line-height: ${({ theme }) => theme.spacing.small}px;
  letter-spacing: 0.3px;
  margin: 0 0 12px;
  font-weight: normal;
  border: none;
  background: transparent;
  text-transform: none;
  height: auto;
`;

const DEFAULT_BLANK_SIZES = [
  'bra-size',
  'bralette-size',
  'bottom-size',
  'lingerie-sleep-size',
];

const AnimatedDiv = styled(animated.div)``;

const InlineSizeCell = ({
  blankSizes = DEFAULT_BLANK_SIZES,
  initialFilters,
}) => {
  const { setFilters } = useProductBrowser();
  const intl = useIntl();
  const { tld } = useDomain();
  const { profile, ...restOfCustomer } = useCustomer();
  const { updateProfile } = useAccountActions();
  const [profileSize, setProfileSize] = useState({
    braSize: {
      bandSize: '',
      cupSize: '',
    },
    braletteSize: '',
    undieSize: '',
    lingerieSleepSize: '',
  });
  const [shouldShowSaveBtn, setShouldShowSaveBtn] = useState(false);
  const [shouldShowContinueText, setShouldShowContinueText] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const infoSizeList = getInfoSizeList(tld, false, false, false);
  const bandSizesList = getBandSizeList(infoSizeList);
  const cupSizesList = getCupSizeList(infoSizeList);
  const nonBraSizesList = getNonBraSizes({ tld, includeKidsAndBaby: false });

  const {
    goToNextStep,
    isFirstStep,
    currentStep,
    isLastStep,
    steps,
    getCurrentStepCategory,
  } = useStepper();

  const assetData = useAsset('profile_category_size_guide', {
    multi: true,
  });
  const { data } = assetData;
  const sizeGuideAssets = data?.assets || [];

  const finalStepStyle = useSpring({
    to: {
      height: isLastStep ? '0px' : '64px',
      opacity: isLastStep ? 0 : 1,
    },
    delay: 2000,
    config: { duration: 1000 },
  });

  const {
    'bra-size': profileBra,
    'bralette-size': profileBralette,
    'bottom-size': profileUndie,
    'lingerie-sleep-size': profileLingerieSleep,
  } = profile;

  const profileData = useMemo(() => {
    const [braSize, braletteSize, undieSize, lingerieSleepSize] = [
      profileBra,
      profileBralette,
      profileUndie,
      profileLingerieSleep,
    ].map(size =>
      size.includes('not_interested') || size.length === 0 ? '' : size
    );

    return {
      braSize: {
        bandSize: braSize.match(/\d+/)?.[0] || '',
        cupSize: braSize.match(/\D+/)?.[0] || '',
      },
      braletteSize: braletteSize,
      undieSize: undieSize,
      lingerieSleepSize: lingerieSleepSize,
    };
  }, [profileBra, profileBralette, profileUndie, profileLingerieSleep]);

  useEffect(() => {
    if (currentStep === steps.length - 2) {
      setTimeout(() => setShouldShowSaveBtn(true), 500);
    }
  }, [currentStep, steps.length]);

  useEffect(() => {
    if (!isFirstStep && !shouldShowContinueText) {
      setTimeout(() => setShouldShowContinueText(true), 500);
    }
  }, [isFirstStep, shouldShowContinueText]);

  useEffect(() => {
    setProfileSize(profileData);
  }, [profileData]);

  useEffect(() => {
    if (isLastStep) {
      const update = async () => {
        const { bandSize, cupSize } = profileSize.braSize;
        const bandCupSize = [bandSize, cupSize].includes('Other')
          ? 'Other'
          : `${bandSize}${cupSize}`;

        try {
          const updatedCustomerData = {
            ...restOfCustomer,
            profile: {
              ...restOfCustomer.profile,
              'bra-size': bandCupSize,
              'bralette-size': profileSize.braletteSize,
              'bottom-size': profileSize.undieSize,
              'lingerie-sleep-size': profileSize.lingerieSleepSize,
            },
          };
          await updateProfile(updatedCustomerData);
          const aggregationValues = getAggregationValues(
            updatedCustomerData.profile
          );
          const filters = applyProfileSizeFilters({
            profileSizes: aggregationValues,
            initialFilters,
          });
          setFilters(filters);
        } catch (error) {
          Sentry.captureException(error);
        }
      };

      const timerId = setTimeout(update, 1800);
      return () => {
        clearTimeout(timerId);
      };
    }
  }, [
    initialFilters,
    isLastStep,
    profileSize.braSize,
    profileSize.braletteSize,
    profileSize.lingerieSleepSize,
    profileSize.undieSize,
    restOfCustomer,
    setFilters,
    updateProfile,
  ]);

  const handleBraSizeSelection = event => {
    setProfileSize({
      ...profileSize,
      braSize: {
        ...profileSize.braSize,
        [event.target.name]: event.target.value,
      },
    });
  };

  const handleSizeSelections = event => {
    setProfileSize({
      ...profileSize,
      [event.target.name]: event.target.value,
    });
  };

  const message = shouldShowContinueText ? (
    <FormattedMessage
      id="inline_size_grid.small_text_continue_message"
      defaultMessage="{message} Let’s continue finding what’s in stock"
      values={{
        message: (
          <BoldText>
            <FormattedMessage
              id="inline_size_grid.small_text_continue_thank_you_text"
              defaultMessage="Thank you!"
            />
          </BoldText>
        ),
      }}
    />
  ) : (
    <FormattedMessage
      id="inline_size_grid.small_text_message"
      defaultMessage="{name}Let's help you find what's in stock in your size."
      values={{
        name:
          restOfCustomer.firstName.length > 0 ? (
            <BoldText>
              <FormattedMessage
                id="inline_size_grid.small_text_greeting"
                defaultMessage="Hi {name}!"
                values={{ name: restOfCustomer.firstName }}
              />
            </BoldText>
          ) : null,
      }}
    />
  );

  const showNextOrSaveText = () => {
    if (shouldShowSaveBtn) {
      return (
        <FormattedMessage
          id="inline_size_grid.save_btn_text"
          defaultMessage="Save"
        />
      );
    }

    return (
      <FormattedMessage
        id="inline_size_grid.next_btn_text"
        defaultMessage="Next"
      />
    );
  };

  const toggleModal = event => {
    event.preventDefault();
    setIsModalOpen(prevState => !prevState);
  };

  return (
    <>
      <Stepper>
        <Stepper.Steps>
          <Stepper.Step
            id="bra"
            categoryLabel="bras"
            skip={!blankSizes.includes('bra-size')}
          >
            <StyledSizeInput
              bandSizeList={bandSizesList}
              cupSizeList={cupSizesList}
              value={profileSize.braSize}
              onChange={handleBraSizeSelection}
            >
              <StyledTextWrapper>
                <StyledSmallText>{message}</StyledSmallText>
                <StyledIconWrapper>
                  <SizeInput.Icon name="bra" />
                </StyledIconWrapper>
                <StyledLargeText>
                  <FormattedMessage
                    defaultMessage="What's your bra size?"
                    id="inline_size_grid.bra_size_large_text"
                  />
                </StyledLargeText>
              </StyledTextWrapper>
              <BraSelectWrapper>
                <BraSizeSelection
                  name="bandSize"
                  placeholder={intl.formatMessage({
                    id: 'inline_size_grid.select',
                    defaultMessage: 'Select',
                  })}
                  label={
                    <FormattedMessage
                      id="inline_size_grid.bra_size_band_label"
                      defaultMessage="Band"
                    />
                  }
                />
                <BraSizeSelection
                  name="cupSize"
                  placeholder={intl.formatMessage({
                    id: 'inline_size_grid.select',
                    defaultMessage: 'Select',
                  })}
                  label={
                    <FormattedMessage
                      id="inline_size_grid.bra_size_cup_label"
                      defaultMessage="Cup"
                    />
                  }
                />
              </BraSelectWrapper>
              <SavedButton
                onClick={goToNextStep}
                disabled={
                  !profileSize.braSize.bandSize.length ||
                  !profileSize.braSize.cupSize.length
                }
              >
                {showNextOrSaveText()}
              </SavedButton>
              <StyledSizeGuideBtn onClick={toggleModal}>
                <FormattedMessage
                  id="inline_size_grid.size_guide"
                  defaultMessage="Need help finding your size?"
                />
              </StyledSizeGuideBtn>
            </StyledSizeInput>
          </Stepper.Step>
          <Stepper.Step
            id="bralette"
            categoryLabel="bralettes"
            skip={!blankSizes.includes('bralette-size')}
          >
            <StyledSizeInput
              value={profileSize.braletteSize}
              options={nonBraSizesList}
              onChange={handleSizeSelections}
            >
              <StyledTextWrapper>
                <StyledSmallText>{message}</StyledSmallText>
                <StyledIconWrapper>
                  <SizeInput.Icon name="bralette" />
                </StyledIconWrapper>
                <StyledLargeText>
                  <FormattedMessage
                    defaultMessage="What's  your bralette size?"
                    id="inline_size_grid.bralette_size_large_text"
                  />
                </StyledLargeText>
              </StyledTextWrapper>
              <StyledSelectWrapper>
                <SizeInput.OneSizeCheckboxes
                  name="braletteSize"
                  inputStyle={inputStyle}
                />
              </StyledSelectWrapper>
              <SavedButton
                onClick={goToNextStep}
                disabled={!profileSize.braletteSize.length}
              >
                {showNextOrSaveText()}
              </SavedButton>
              <StyledSizeGuideBtn onClick={toggleModal}>
                <FormattedMessage
                  id="inline_size_grid.size_guide"
                  defaultMessage="Need help finding your size?"
                />
              </StyledSizeGuideBtn>
            </StyledSizeInput>
          </Stepper.Step>
          <Stepper.Step
            id="undies"
            categoryLabel="undies"
            skip={!blankSizes.includes('bottom-size')}
          >
            <StyledSizeInput
              value={profileSize.undieSize}
              options={nonBraSizesList}
              onChange={handleSizeSelections}
            >
              <StyledTextWrapper>
                <StyledSmallText>{message}</StyledSmallText>
                <StyledIconWrapper>
                  <SizeInput.Icon name="undie_alt" />
                </StyledIconWrapper>
                <StyledLargeText>
                  <FormattedMessage
                    defaultMessage="What's your undie or boxer size?"
                    id="inline_size_grid.undie_size_large_text"
                  />
                </StyledLargeText>
              </StyledTextWrapper>
              <StyledSelectWrapper>
                <SizeInput.OneSizeCheckboxes
                  name="undieSize"
                  inputStyle={inputStyle}
                />
              </StyledSelectWrapper>
              <SavedButton
                onClick={goToNextStep}
                disabled={!profileSize.undieSize.length}
              >
                {showNextOrSaveText()}
              </SavedButton>
              <StyledSizeGuideBtn onClick={toggleModal}>
                <FormattedMessage
                  id="inline_size_grid.size_guide"
                  defaultMessage="Need help finding your size?"
                />
              </StyledSizeGuideBtn>
            </StyledSizeInput>
          </Stepper.Step>
          <Stepper.Step
            id="lingeriesleep"
            categoryLabel="lingeriesleep"
            skip={!blankSizes.includes('lingerie-sleep-size')}
          >
            <StyledSizeInput
              value={profileSize.lingerieSleepSize}
              options={nonBraSizesList}
              onChange={handleSizeSelections}
            >
              <StyledTextWrapper>
                <StyledSmallText>{message}</StyledSmallText>
                <StyledIconWrapper>
                  <SizeInput.Icon name="lingerie_sleep" />
                </StyledIconWrapper>
                <StyledLargeText>
                  <FormattedMessage
                    defaultMessage="What's your lingerie or sleep size?"
                    id="inline_size_grid.lingerie_sleep_size_large_text"
                  />
                </StyledLargeText>
              </StyledTextWrapper>
              <StyledSelectWrapper>
                <SizeInput.OneSizeCheckboxes
                  name="lingerieSleepSize"
                  inputStyle={inputStyle}
                />
              </StyledSelectWrapper>
              <SavedButton
                onClick={goToNextStep}
                disabled={!profileSize.lingerieSleepSize.length}
              >
                {showNextOrSaveText()}
              </SavedButton>
              <StyledSizeGuideBtn onClick={toggleModal}>
                <FormattedMessage
                  id="inline_size_grid.size_guide"
                  defaultMessage="Need help finding your size?"
                />
              </StyledSizeGuideBtn>
            </StyledSizeInput>
          </Stepper.Step>
          <Stepper.Step id="final">
            <AnimatedDiv style={{ overflow: 'hidden', ...finalStepStyle }}>
              <StyledFinalStepWrapper>
                <StyledFinalStepText>
                  <FormattedMessage
                    id="inline_size_grid.small_text_final_message"
                    defaultMessage="Thank you! {message}"
                    values={{
                      message: (
                        <BoldText>
                          <FormattedMessage
                            id="inline_size_grid.small_text_final_bold_text"
                            defaultMessage="Your sizes have been saved"
                          />
                        </BoldText>
                      ),
                    }}
                  />
                </StyledFinalStepText>
              </StyledFinalStepWrapper>
            </AnimatedDiv>
          </Stepper.Step>
        </Stepper.Steps>
      </Stepper>
      <SizeGuideModal
        isTabbed
        assets={sizeGuideAssets}
        title="Inline Size Guide Modal"
        isOpen={isModalOpen}
        onClose={toggleModal}
        selectedTabSizeCategory={getCurrentStepCategory()}
      />
    </>
  );
};

InlineSizeCell.propTypes = {
  blankSizes: PropTypes.array,
  initialFilters: PropTypes.object.isRequired,
};

export default React.memo(InlineSizeCell);
